let predictiveSearchInstanceCount = 0;

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.instanceId = `predictive-search-${++predictiveSearchInstanceCount}`;
    this.cachedResults = Object.create(null);
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.resultsId = this.dataset.predictiveResultsId || `${this.instanceId}-results`;
    this.nextResultId = 0;
    this.requestVersion = 0;
    this.isOpen = false;
    this.popupWanted = false;
    this.searchTerm = '';
    this.emptyMarkup = this.predictiveSearchResults?.querySelector('.predictive-search-dropdown')?.outerHTML;
    this.renderedTerm = this.emptyMarkup ? '' : null;
    this.statusElement = this.querySelector('.predictive-search-status');
    this.onOutsidePointer = event => {
      if (!this.contains(event.target)) this.close();
    };
    if (!this.input || !this.predictiveSearchResults) return;

    // A dialog with navigation links and tabs uses real focus, not virtual options.
    this.predictiveSearchResults.id = this.resultsId;
    this.input.setAttribute('aria-controls', this.resultsId);
    this.input.removeAttribute('aria-owns');
    this.input.removeAttribute('aria-activedescendant');
    this.scopeResultIds();
    this.setupEventListeners();
    this.close();
  }

  connectedCallback() {
    if (!this.predictiveSearchResults) return;
    document.addEventListener('pointerdown', this.onOutsidePointer);
    this.parentDisclosure = this.closest('details');
    this.parentDrawer = this.closest('side-drawer');
    this.parentObserver = new MutationObserver(() => {
      if (!this.isHostOpen()) this.close();
    });
    if (this.parentDisclosure) this.parentObserver.observe(this.parentDisclosure, { attributes: true, attributeFilter: ['open'] });
    if (this.parentDrawer) this.parentObserver.observe(this.parentDrawer, { attributes: true, attributeFilter: ['class'] });
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.onOutsidePointer);
    this.parentObserver?.disconnect();
    clearTimeout(this.statusTimer);
    this.close();
  }

  isHostOpen() {
    return (!this.parentDisclosure || this.parentDisclosure.open) &&
      (!this.parentDrawer || this.parentDrawer.classList.contains('open'));
  }

  canShowResults(term = this.getQuery()) {
    return this.isConnected && this.popupWanted && this.isHostOpen() &&
      this.contains(document.activeElement) && this.getQuery() === term;
  }

  scopeResultIds(root = this.predictiveSearchResults) {
    if (!root) return;
    const idMap = new Map();
    root.querySelectorAll('[id]').forEach(element => {
      const previousId = element.id;
      const baseId = element.dataset.predictiveBaseId || previousId;
      element.dataset.predictiveBaseId = baseId;
      if (element.dataset.predictiveScope !== this.instanceId) {
        element.id = `${baseId}-${this.instanceId}-${++this.nextResultId}`;
        element.dataset.predictiveScope = this.instanceId;
      }
      if (!idMap.has(baseId)) idMap.set(baseId, element.id);
      idMap.set(previousId, element.id);
    });
    ['aria-controls', 'aria-labelledby', 'aria-describedby'].forEach(attribute => {
      root.querySelectorAll(`[${attribute}]`).forEach(element => {
        const originalAttribute = `data-predictive-${attribute}`;
        const references = element.getAttribute(originalAttribute) || element.getAttribute(attribute);
        element.setAttribute(originalAttribute, references);
        element.setAttribute(attribute, references.split(/\s+/).map(id => idMap.get(id) || id).join(' '));
      });
    });
    // Tab hrefs must follow scoped panel IDs too, including cached HTML.
    root.querySelectorAll('a[href^="#"]').forEach(link => {
      const reference = link.dataset.predictiveHref || link.getAttribute('href').slice(1);
      link.dataset.predictiveHref = reference;
      if (idMap.has(reference)) link.setAttribute('href', `#${idMap.get(reference)}`);
    });
    this.prepareTabs(root);
  }

  prepareTabs(root) {
    root.querySelectorAll('.predictive-search-tabs').forEach(component => {
      const header = component.querySelector('.tabs-product-header');
      if (!header) return;
      header.setAttribute('role', 'tablist');
      header.setAttribute('aria-label', this.predictiveSearchResults.getAttribute('aria-label'));
      component.querySelectorAll('.tabs-component-panel-trigger').forEach(tab => {
        const panelId = tab.getAttribute('href')?.slice(1);
        const panel = Array.from(component.querySelectorAll('[data-tabs-panel]')).find(item => item.id === panelId);
        if (!panel) return;
        const active = tab.classList.contains('--active');
        if (!tab.id) tab.id = `${this.instanceId}-tab-${++this.nextResultId}`;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panel.id);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        panel.hidden = !active;
        panel.inert = !active;
      });
    });
  }

  activateTab(tab) {
    const component = tab.closest('.predictive-search-tabs');
    if (!component) return;
    const panelId = tab.getAttribute('aria-controls');
    component.querySelectorAll('.tabs-component-panel-trigger').forEach(item => item.classList.toggle('--active', item === tab));
    component.querySelectorAll('[data-tabs-panel]').forEach(panel => {
      const active = panel.id === panelId;
      panel.classList.toggle('--active', active);
      if (active) {
        const template = panel.querySelector('template.tabs-component-template');
        if (template && !panel.hasAttribute('data-rendered')) {
          panel.appendChild(template.content.cloneNode(true));
          panel.setAttribute('data-rendered', 'true');
        }
      }
    });
    this.scopeResultIds();
  }

  setupEventListeners() {
    this.input.form?.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('click', this.onFocus.bind(this));
    // Invalidate old responses immediately, before the shared input debounce.
    this.input.addEventListener('input', () => {
      this.cancelPending();
      this.popupWanted = true;
      super.onChange();
    });
    this.addEventListener('focusout', () => {
      setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); }, 0);
    });
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('keyup', event => {
      if (event.key === 'Escape' && this.handledEscape) {
        event.stopPropagation();
        this.handledEscape = false;
      }
    });
    // Own only search tabs; never reinitialize the shared TabsComponent per key.
    this.addEventListener('click', event => {
      const tab = event.target.closest('.predictive-search-tabs [role="tab"]');
      if (!tab) return;
      event.preventDefault();
      event.stopPropagation();
      this.activateTab(tab);
    }, true);
  }

  getQuery() { return this.input.value.trim(); }

  onChange() {
    super.onChange();
    this.searchTerm = this.getQuery();
    if (!this.canShowResults()) return;
    if (this.hasAttribute('loading') && this.pendingTerm === this.searchTerm) return;
    if (!this.searchTerm) this.renderEmptyState();
    else this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery()) event.preventDefault();
    else this.close();
  }

  onFormReset(event) {
    event.preventDefault();
    this.cancelPending();
    this.input.value = '';
    this.searchTerm = '';
    super.onChange();
    this.suppressFocus = true;
    this.input.focus();
    this.suppressFocus = false;
    this.popupWanted = true;
    this.renderEmptyState();
  }

  onFocus() {
    if (this.suppressFocus) return;
    this.popupWanted = true;
    if (this.renderedTerm === this.getQuery()) this.open();
    else this.onChange();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      const modal = this.closest('details-modal');
      if (this.isOpen || this.popupWanted) {
        this.suppressFocus = true;
        this.input.focus();
        this.suppressFocus = false;
        this.close();
      } else if (modal) {
        modal.open = false;
      } else return;
      event.preventDefault();
      event.stopPropagation();
      this.handledEscape = true;
      return;
    }
    const tab = event.target.closest('[role="tab"]');
    if (tab && ['ArrowLeft', 'ArrowRight', 'Home', 'End', ' '].includes(event.key)) {
      event.preventDefault();
      const tabs = Array.from(tab.closest('[role="tablist"]').querySelectorAll('[role="tab"]'));
      const direction = getComputedStyle(tab).direction === 'rtl' ? -1 : 1;
      let index = tabs.indexOf(tab);
      if (event.key === 'Home') index = 0;
      if (event.key === 'End') index = tabs.length - 1;
      if (event.key === 'ArrowRight') index = (index + direction + tabs.length) % tabs.length;
      if (event.key === 'ArrowLeft') index = (index - direction + tabs.length) % tabs.length;
      this.activateTab(tabs[index]);
      tabs[index].focus();
      return;
    }
    if (event.target !== this.input || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    event.preventDefault();
    if (!this.isOpen) this.onFocus();
    if (!this.isOpen) return;
    const targets = Array.from(this.predictiveSearchResults.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]'))
      .filter(element => element.tabIndex >= 0 && !element.closest('[hidden], [inert]') && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
    const target = event.key === 'ArrowUp' ? targets[targets.length - 1] : targets[0];
    target?.focus();
  }

  cancelPending() {
    this.requestVersion++;
    this.abortController?.abort();
    this.pendingTerm = null;
    this.removeAttribute('loading');
    this.predictiveSearchResults?.removeAttribute('aria-busy');
  }

  beginRequest() {
    this.cancelPending();
    this.abortController = new AbortController();
    this.pendingTerm = this.getQuery();
    this.setAttribute('loading', 'true');
    this.predictiveSearchResults.setAttribute('aria-busy', 'true');
    this.setLiveRegionText(this.getAttribute('data-loading-text'));
    this.open();
    return this.requestVersion;
  }

  async renderEmptyState() {
    if (!this.canShowResults('')) return;
    if (this.emptyMarkup) {
      this.cancelPending();
      this.renderSearchResults(this.emptyMarkup, '');
      return;
    }
    const version = this.beginRequest();
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.searchParams.set('section_id', 'predictive-search-empty');
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: this.abortController.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = page.querySelector('#shopify-section-predictive-search-empty .predictive-search-dropdown');
      if (!content) throw new Error('Missing predictive search section');
      this.emptyMarkup = content.outerHTML;
      if (version === this.requestVersion && this.canShowResults('')) this.renderSearchResults(this.emptyMarkup, '');
    } catch (error) {
      if (version === this.requestVersion && error.name !== 'AbortError') this.close();
    }
  }

  async getSearchResults(searchTerm) {
    if (!this.canShowResults(searchTerm)) return;
    if (this.getAttribute('unsupported-locale') === 'true') { this.close(); return; }
    // Preserve spaces, case and locale rather than conflate different queries.
    const queryKey = `${window.SNMarket?.key || ''}:${routes.predictive_search_url}:${searchTerm}`;
    if (this.cachedResults[queryKey]) {
      this.cancelPending();
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm);
      return;
    }
    const version = this.beginRequest();
    try {
      const url = new URL(routes.predictive_search_url, window.location.origin);
      url.searchParams.set('q', searchTerm);
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('section_id', 'predictive-search');
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: this.abortController.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (version !== this.requestVersion) return;
      if (response.status === 417) {
        this.setAttribute('unsupported-locale', 'true');
        this.close();
        return;
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const section = page.querySelector('#shopify-section-predictive-search');
      if (!section) throw new Error('Missing predictive search section');
      if (version !== this.requestVersion || !this.canShowResults(searchTerm)) return;
      document.querySelectorAll('predictive-search').forEach(instance => {
        if (instance.cachedResults) instance.cachedResults[queryKey] = section.innerHTML;
      });
      this.renderSearchResults(section.innerHTML, searchTerm);
    } catch (error) {
      // Native form submission still works when suggestions are unavailable.
      if (version === this.requestVersion && error.name !== 'AbortError') this.close();
    }
  }

  setLiveRegionText(text) {
    if (!this.statusElement || !text) return;
    clearTimeout(this.statusTimer);
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = text;
    this.statusTimer = setTimeout(() => this.statusElement.setAttribute('aria-hidden', 'true'), 1000);
  }

  renderSearchResults(markup, term) {
    if (!this.canShowResults(term)) return;
    const fragment = document.createElement('template');
    fragment.innerHTML = markup;
    this.scopeResultIds(fragment.content);
    if (this.predictiveSearchResults.contains(document.activeElement)) {
      this.suppressFocus = true;
      this.input.focus();
      this.suppressFocus = false;
    }
    this.predictiveSearchResults.replaceChildren(fragment.content);
    this.renderedTerm = term;
    this.pendingTerm = null;
    this.setAttribute('results', 'true');
    this.removeAttribute('loading');
    this.predictiveSearchResults.removeAttribute('aria-busy');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent);
    this.open();
  }

  open() {
    if (!this.predictiveSearchResults || !this.canShowResults()) return;
    const headerBottom = document.querySelector('.header-group-section')?.getBoundingClientRect().bottom || this.input.getBoundingClientRect().bottom;
    this.predictiveSearchResults.style.maxHeight = `${Math.max(160, window.innerHeight - Math.max(0, headerBottom))}px`;
    this.predictiveSearchResults.hidden = false;
    this.predictiveSearchResults.inert = false;
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.popupWanted = false;
    this.cancelPending();
    this.isOpen = false;
    if (clearSearchTerm && this.input) {
      this.input.value = '';
      this.searchTerm = '';
      this.removeAttribute('results');
    }
    this.removeAttribute('open');
    this.input?.setAttribute('aria-expanded', 'false');
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.hidden = true;
      this.predictiveSearchResults.inert = true;
      this.predictiveSearchResults.style.removeProperty('max-height');
    }
    clearTimeout(this.statusTimer);
    this.statusElement?.setAttribute('aria-hidden', 'true');
  }

  closeResults(clearSearchTerm = false) { this.close(clearSearchTerm); }
}
if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
